import { useState } from 'react';
import { useNavigate } from 'react-router';

// material-ui
import {
  Drawer,
  Tabs,
  Tab,
  Box,
  List,
  ListItem,
  Stack,
  Checkbox,
  Button,
  useTheme,
  Typography,
  Divider,
  useMediaQuery,
  Grid,
  InputLabel,
  TextField
} from '@mui/material';

// third-party
import { FormattedMessage, useIntl } from 'react-intl';

// project-imports
import Avatar from 'components/@extended/Avatar';
import IconButton from 'components/@extended/IconButton';
import MainCard from 'components/MainCard';
import SimpleBar from 'components/third-party/SimpleBar';
import { HEADER_HEIGHT } from 'config';
import { DTDevelopmentItem, useDTDevelopment } from 'contexts/DTDevelopmentContext';

// assets
import { Add, Buildings, Location, Trash } from 'iconsax-react';

interface ItemProps {
  item: DTDevelopmentItem;
  removeItem: (id: string) => void;
  toggleHighlight?: (id: string) => void;
}

const Item = ({ item, removeItem, toggleHighlight }: ItemProps) => {
  return (
    <MainCard content={false} sx={{ position: 'relative', width: '100%', p: 2, bgcolor: 'background.default' }}>
      <Stack direction="row" spacing={2}>
        <Avatar
          // src={`https://hh-media-bucket.s3.eu-west-3.amazonaws.com/developments_v2/${item.id}/logo.jpg`}
          src={`https://medianewbuild.com/file/hh-media-bucket/developments_v2/${item.id}/logo.jpg`}
          alt={`Logo ${item?.additional?.name}`}
          size="xl"
          variant="rounded"
          children={<Buildings variant="Bulk" />}
        />
        {/* <Checkbox checked={item.highlighted} onChange={() => toggleHighlight(item.id)} /> */}
        <Stack spacing={0.5} sx={{ width: '100%' }} justifyContent="center">
          <Typography
            fontWeight="bold"
            noWrap
            onClick={() => {
              window.open(`/developments/${item.id}`, '_blank');
            }}
            sx={{ cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
          >
            {item?.additional?.name}
          </Typography>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Location size={14} variant="Bulk" />
            <Typography noWrap>{item?.additional?.location}</Typography>
          </Stack>
        </Stack>
        <IconButton color="error" sx={{ position: 'absolute', top: 16, right: 16, p: 0 }} onClick={() => removeItem(item.id)}>
          <Trash size={28} />
        </IconButton>
      </Stack>
    </MainCard>
  );
};

// ==============================|| OFFER - DRAWER ||============================== //

const DTDevelopmentDrawer = () => {
  const theme = useTheme();
  const matchDownMd = useMediaQuery(theme.breakpoints.down('md'));

  const intl = useIntl();

  const navigate = useNavigate();

  const {
    dtDevelopment,
    dtDevelopmentName,
    setDTDevelopmentName,
    isDrawerOpen,
    isEditing,
    toggleDTDevelopmentDrawer,
    removeItem,
    toggleHighlight,
    getDTDevelopmentItemCount,
    saveDTDevelopment,
    clearDTDevelopment
  } = useDTDevelopment();
  const [tabIndex, setTabIndex] = useState(0);

  const developments = dtDevelopment.filter((item) => item.type === 'development');
  const properties = dtDevelopment.filter((item) => item.type === 'property');

  const goToDevelopments = () => {
    toggleDTDevelopmentDrawer();
    navigate('/developments');
  };

  return (
    <Drawer
      anchor="right"
      open={isDrawerOpen}
      onClose={toggleDTDevelopmentDrawer}
      PaperProps={{
        sx: {
          width: matchDownMd ? '100%' : '40%',
          '& .MuiPaper-root': {
            height: '100%'
          }
        }
      }}
    >
      <MainCard content={false} border={false} style={{ position: 'relative' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5} sx={{ p: 2.5 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Buildings variant="Bulk" />
            <Typography variant="h5">
              {/* <FormattedMessage id={isEditing ? 'offers.edit' : 'offers.new'} /> */}
              {isEditing
                ? intl.formatMessage({ id: 'discoveryTrip.developments.edit' })
                : intl.formatMessage({ id: 'discoveryTrip.developments.new' })}
            </Typography>
          </Stack>
          <IconButton color="error" sx={{ p: 0 }} onClick={toggleDTDevelopmentDrawer}>
            <Add size={28} style={{ transform: 'rotate(45deg)' }} />
          </IconButton>
        </Stack>

        <Divider />

        <SimpleBar
          sx={{
            height: `calc(100vh - ${HEADER_HEIGHT * 2}px)`,
            '& .simplebar-content': {
              display: 'flex',
              flexDirection: 'column'
            }
          }}
        >
          <Box sx={{ py: 2, height: `calc(100vh - ${HEADER_HEIGHT * 2}px)` }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sx={{ mx: 2 }}>
                <Stack spacing={1}>
                  <InputLabel>
                    <FormattedMessage id="common.others.name" />
                  </InputLabel>
                  <TextField
                    id="offerName"
                    name="offerName"
                    placeholder={intl.formatMessage({ id: 'offers.optional' })}
                    value={dtDevelopmentName || ''}
                    onChange={(event: any) => setDTDevelopmentName(event.target.value)}
                    fullWidth
                  />
                </Stack>
              </Grid>
              {/* <Grid item xs={12} sx={{ mx: 2 }}>
                <Stack spacing={1}>
                  <InputLabel>
                    <FormattedMessage id="common.others.email" />
                  </InputLabel>
                  <TextField
                    id="customerEmail"
                    name="customerEmail"
                    placeholder={intl.formatMessage({ id: 'offers.optional' })}
                    value={customerEmail || ''}
                    onChange={(event: any) => setCustomerEmail(event.target.value)}
                    fullWidth
                  />
                </Stack>
              </Grid> */}

              <Divider sx={{ width: '100%', mt: 3 }} />

              <Grid item xs={12} sx={{ mt: -1.5 }}>
                <Tabs value={tabIndex} onChange={(e, index) => setTabIndex(index)}>
                  <Tab label={intl.formatMessage({ id: 'developments.title' })} />
                  {/* <Tab label={intl.formatMessage({ id: 'developments.properties.title' })} disabled /> */}
                </Tabs>

                {/* [ TO-DO ] - Make dynamic switch between developments and properties (when properties are included) */}
                <Box role="tabpanel" hidden={tabIndex !== 0}>
                  {dtDevelopment.length === 0 ? (
                    <Box p={2}>
                      <Button variant="contained" startIcon={<Buildings variant="Bulk" />} onClick={goToDevelopments} fullWidth>
                        <FormattedMessage id="offers.goToDevelopments" />
                      </Button>
                    </Box>
                  ) : (
                    <List>
                      {developments.map((item) => (
                        <ListItem key={item.id} sx={{ pb: 0 }}>
                          {/* <Checkbox checked={item.highlighted} onChange={() => toggleHighlight(item.id)} />
                    {item.id}
                    <Button onClick={() => removeItem(item.id)}>Eliminar</Button> */}
                          <Item item={item} toggleHighlight={toggleHighlight} removeItem={removeItem} />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Box>
                <Box role="tabpanel" hidden={tabIndex !== 1}>
                  <List>
                    {properties.map((item) => (
                      <ListItem key={item.id}>
                        <Checkbox checked={item.highlighted} onChange={() => toggleHighlight(item.id)} />
                        {item.id}
                        <Button onClick={() => removeItem(item.id)}>Eliminar</Button>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </SimpleBar>

        <Grid container sx={{ p: 2.5, position: 'absolute', bottom: 0, width: '100%' }}>
          <Grid item xs={12}>
            <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="center">
              <Button color="error" onClick={() => clearDTDevelopment(true)}>
                <FormattedMessage id="common.others.cancel" />
              </Button>
              <Button variant="contained" onClick={saveDTDevelopment} disabled={getDTDevelopmentItemCount() === 0}>
                <FormattedMessage id="common.others.save" />
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </MainCard>
    </Drawer>
  );
};

export default DTDevelopmentDrawer;
